/*
 * FSR: Cart drawer (sections/fsr-cart-drawer.liquid).
 * Opens after add to cart (theme event ARMADA:CART:ADD) and when a cart link is
 * clicked (fsr:cart:open from the head guard); content is refreshed via the
 * Section Rendering API.
 */
class FsrCartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.panel = this.querySelector('.fsr-cart-drawer__panel');

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-fsr-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const removeButton = event.target.closest('[data-fsr-cart-remove]');
      if (removeButton) this.removeItem(removeButton.dataset.fsrCartRemove);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    });

    document.addEventListener('ARMADA:CART:ADD', () => this.refreshAndOpen());

    // Add to cart failed (e.g. 422: the one-of-a-kind piece is already in the cart).
    // The theme only logs this, so show the cart with a short explanation instead.
    document.addEventListener('ARMADA:CART:ERROR', (event) => {
      const error = event.detail?.error;
      if (!error?.data) return;
      const message = error.data.status === 422 ? this.dataset.msgInCart : this.dataset.msgError;
      this.refresh().catch(() => {}).then(() => {
        this.showNotice(message);
        this.open();
      });
    });

    // Cart links are caught early by snippets/fsr-cart-drawer-guard.liquid (in <head>),
    // which asks the drawer to open via this event or queues the request.
    document.addEventListener('fsr:cart:open', () => this.refreshAndOpen());
    if (window.fsrCartPending) {
      window.fsrCartPending = false;
      this.refreshAndOpen();
    }
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  refreshAndOpen() {
    // Open even if refreshing the content fails, so the shopper always gets feedback.
    return this.refresh().catch(() => {}).then(() => this.open());
  }

  open() {
    if (window.eight?.eventsEngine) {
      window.eight.eventsEngine.emit(window.eight.constants.events['ARMADA:MODAL:CLOSE:ALL']);
    }
    this.previousFocus = document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('fsr-cart-drawer-open');
    this.panel.focus();
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('fsr-cart-drawer-open');
    if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus();
  }

  setLoading(loading) {
    const content = this.querySelector('[data-fsr-cart-content]');
    if (content) content.classList.toggle('is-loading', loading);
  }

  async refresh() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=${this.sectionId}`);
    if (!response.ok) return;
    const sections = await response.json();
    this.render(sections[this.sectionId]);
  }

  render(html) {
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fsr-cart-content]');
    const current = this.querySelector('[data-fsr-cart-content]');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  }

  showNotice(message) {
    const header = this.querySelector('.fsr-cart-drawer__header');
    if (!header || !message) return;
    const notice = document.createElement('p');
    notice.className = 'fsr-cart-drawer__notice';
    notice.setAttribute('role', 'alert');
    notice.textContent = message;
    header.after(notice);
  }

  async removeItem(key) {
    this.setLoading(true);
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0, sections: [this.sectionId] }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();
      this.render(cart.sections && cart.sections[this.sectionId]);
      // Updates the header cart count via the theme's cart engine.
      if (window.eight?.cartEngine) window.eight.cartEngine.getCart();
    } catch (error) {
      console.error('Cart drawer: could not remove item', error);
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('fsr-cart-drawer')) customElements.define('fsr-cart-drawer', FsrCartDrawer);
